import Add from '@mui/icons-material/Add'
import RefreshIcon from '@mui/icons-material/Refresh'
import {
  Box,
  Button,
  IconButton,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import React from 'react'

import ExternalLink from '@/app/component/atom/external-link'
import Loader from '@/app/component/atom/loader'
import TabHeader from '@/app/component/atom/tab-header'
import { useConfirmation } from '@/app/contexts/confirmation.context'
import { APIKey, UpdateAPIKeyPayload } from '@/app/module/user/types'

import ApiKeyModal from './api-key-modal'
import ApiKeyRow from './api-key-row'

type Props = {
  apiKeys: APIKey[]
  loading: boolean

  onCreate: () => Promise<void>
  onDelete: (id: number) => Promise<void>
  onEdit: (tokenId: number, item: UpdateAPIKeyPayload) => Promise<void>
  onRefresh: () => Promise<void>
}

const ApiKeys: React.FC<Props> = ({ apiKeys, loading, onCreate, onDelete, onEdit, onRefresh }) => {
  const [currentAPIKey, setCurrentAPIKey] = React.useState<APIKey | null>(null)
  const createConfirmation = useConfirmation()

  const handleEdit = React.useCallback(
    (apiKey: APIKey, values: UpdateAPIKeyPayload) => {
      const { active, expires, name } = values
      const cleanValues: UpdateAPIKeyPayload = {
        active,
        name,
      }
      if (expires) {
        cleanValues.expires = expires
      }

      return onEdit(apiKey.id, cleanValues)
    },
    [onEdit],
  )
  const handleDeleteClick = React.useCallback(
    async (id: number) => {
      const confirmed = await createConfirmation({
        title: 'Delete Token',
        content: 'Are you sure you want to delete this token?',
        confirmButtonLabel: 'Confirm',
        cancelButtonLabel: 'Cancel',
      })

      if (confirmed) {
        await onDelete(id)
      }
    },
    [createConfirmation, onDelete],
  )

  return (
    <Container>
      <TabHeader
        title="Personal Access Tokens"
        action={
          <>
            <Button disabled={loading} startIcon={<Add />} variant="contained" onClick={onCreate}>
              New Token
            </Button>
            <Tooltip title="Refresh">
              <span>
                <IconButton data-testid="api-keys-refresh" disabled={loading} size="small" onClick={onRefresh}>
                  <RefreshIcon />
                </IconButton>
              </span>
            </Tooltip>
          </>
        }
      />
      <Content>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: '15px',
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography>
              You can use our APIs to integrate engageSPARK securely with 3rd-party systems, such as your CRM, or to
              automate tasks.
              <br />
              For example, you might create contacts, subscribe them to campaigns, or send airtime top-ups. To use our
              APIs, you'll need a personal access token, which you can find below.{' '}
              <ExternalLink href="https://www.engagespark.com/support/how-can-i-use-your-api/" target="_blank">
                Learn more about our APIs here.
              </ExternalLink>
            </Typography>
            <Typography>
              <b>Important:</b> These personal access tokens grant access to all your engageSPARK organizations and all
              their data under your name and with all your access rights. Keep these personal access tokens as safe and
              private as your login password.
            </Typography>
          </Box>
        </Box>
        <Scrollable>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell width="20%">Name</TableCell>
                  <TableCell width="20%">Key</TableCell>
                  <TableCell align="center" width="15%">
                    Created
                  </TableCell>
                  <TableCell align="center" width="15%">
                    Expires
                  </TableCell>
                  <TableCell align="center" width="10%">
                    Live
                  </TableCell>
                  <TableCell align="center" width="10%">
                    Enabled
                  </TableCell>
                  <TableCell align="center" width="10%">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {apiKeys.map((apiKey) => (
                  <ApiKeyRow
                    key={apiKey.id}
                    apiKey={apiKey}
                    onDelete={handleDeleteClick}
                    onEditClick={setCurrentAPIKey}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Scrollable>
        {loading && <Loader />}
        {currentAPIKey && (
          <ApiKeyModal
            apiKey={currentAPIKey}
            loading={loading}
            onClose={() => setCurrentAPIKey(null)}
            onSubmit={handleEdit}
          />
        )}
      </Content>
    </Container>
  )
}

const Container = styled(Card)({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  minHeight: 0,
  overflow: 'hidden',
})

const Content = styled(CardContent)({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: 0, // allow the inner flex child to shrink and enable scrolling
  overflow: 'hidden',
})

const Scrollable = styled('div')({
  flex: 1,
  minHeight: 0,
  display: 'flex',
  overflow: 'hidden',
})

export default ApiKeys
